import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ActiveImpersonationSession } from '@shared/types';

/**
 * Impersonation Store
 * Manages super admin impersonation state
 * Persisted to localStorage to maintain state across page reloads
 */

interface ImpersonatedUser {
  id: string;
  name: string;
  email: string;
  companyId: string;
  companyName: string;
}

interface ImpersonationStore {
  isImpersonating: boolean;
  session: ActiveImpersonationSession | null;
  impersonatedUser: ImpersonatedUser | null;
  superAdminId: string | null;
  startImpersonation: (
    session: ActiveImpersonationSession,
    user: ImpersonatedUser,
    superAdminId: string
  ) => void;
  endImpersonation: () => void;
  checkExpiry: () => boolean; // Returns true if session is expired
}

export const useImpersonationStore = create<ImpersonationStore>()(
  persist(
    (set, get) => ({
      isImpersonating: false,
      session: null,
      impersonatedUser: null,
      superAdminId: null,

      startImpersonation: (session, user, superAdminId) => {
        console.log('[IMPERSONATION STORE] Starting impersonation', {
          sessionId: session.id,
          user: user.name,
          company: user.companyName,
        });

        set({
          isImpersonating: true,
          session,
          impersonatedUser: user,
          superAdminId,
        });
      },

      endImpersonation: () => {
        console.log('[IMPERSONATION STORE] Ending impersonation');

        set({
          isImpersonating: false,
          session: null,
          impersonatedUser: null,
          superAdminId: null,
        });
      },

      checkExpiry: () => {
        const { session, endImpersonation } = get();

        if (!session) {
          return false;
        }

        const expiresAt = new Date(session.expires_at);
        const now = new Date();

        if (now >= expiresAt) {
          console.warn('[IMPERSONATION STORE] Session expired, ending impersonation');
          endImpersonation();
          return true;
        }

        return false;
      },
    }),
    {
      name: 'impersonation-storage', // localStorage key
      // Only persist session, impersonatedUser, and superAdminId
      partialize: (state) => ({
        session: state.session,
        impersonatedUser: state.impersonatedUser,
        superAdminId: state.superAdminId,
        isImpersonating: state.isImpersonating,
      }),
    }
  )
);

// Auto-check expiry every minute
if (typeof window !== 'undefined') {
  setInterval(() => {
    useImpersonationStore.getState().checkExpiry();
  }, 60000); // 1 minute
}
